import { Controller } from "@hotwired/stimulus"

// Quick-find dropdown on the staff candidate detail page: a staffer types to jump to another
// candidate without leaving the page. Focusing the box dims the page behind a backdrop; typing
// fetches live matches (debounced ~250ms) from the quick-find endpoint and swaps the returned
// fragment into the dropdown. Each in-flight request is aborted before the next fires, so a slow
// earlier response can never overwrite a newer one. Esc or a backdrop click closes it. A blank
// box fetches nothing — the server returns the whole roster on a blank query, so we never send it.
export default class extends Controller {
  static values = { url: String }
  static targets = ["input", "results", "backdrop"]

  open() {
    this.backdropTarget.classList.remove("hidden")
  }

  close() {
    this.abortInFlight()
    clearTimeout(this.debounceTimer)
    this.backdropTarget.classList.add("hidden")
    this.hideResults()
  }

  search() {
    clearTimeout(this.debounceTimer)
    const query = this.inputTarget.value.trim()
    if (query === "") {
      this.abortInFlight()
      this.hideResults()
      return
    }
    this.debounceTimer = setTimeout(() => this.fetchResults(query), 250)
  }

  async fetchResults(query) {
    this.abortInFlight()
    this.abortController = new AbortController()
    const url = `${this.urlValue}?q=${encodeURIComponent(query)}`
    try {
      const response = await fetch(url, {
        credentials: "same-origin",
        headers: { Accept: "text/html" },
        signal: this.abortController.signal,
      })
      if (!response.ok || response.redirected) return
      this.resultsTarget.innerHTML = await response.text()
      this.resultsTarget.classList.remove("hidden")
    } catch (error) {
      if (error.name !== "AbortError") throw error
    }
  }

  hideResults() {
    this.resultsTarget.classList.add("hidden")
    this.resultsTarget.innerHTML = ""
  }

  abortInFlight() {
    if (this.abortController) {
      this.abortController.abort()
      this.abortController = null
    }
  }

  disconnect() {
    clearTimeout(this.debounceTimer)
    this.abortInFlight()
  }
}
